import { useMemo, useState } from 'react';
import { ConfirmButton, Field, Input, OCRConfidence } from '@garnet/ui';
import { parseIdentifier, type ParsedSegment } from '../data/shape.js';
import { ACTIVE_VERTICAL } from '../config/verticals.js';

/**
 * Live identifier parse with the confirm-before-trust shape (PRD §27.2): the carrier is
 * split into DI/PI as you type, each segment shows its parse confidence, and nothing is
 * trusted until the supplier confirms it. Parsing is the pure `parseIdentifier`.
 */
export function IdentifierField({ defaultValue = '' }: { defaultValue?: string }) {
  const V = ACTIVE_VERTICAL;
  const [raw, setRaw] = useState(defaultValue);
  const [confirmed, setConfirmed] = useState<ReadonlySet<string>>(new Set());

  const parsed = useMemo(() => parseIdentifier(raw), [raw]);
  const segments: ParsedSegment[] = parsed.di ? [parsed.di, ...parsed.pi] : parsed.pi;

  const toggle = (key: string): void =>
    setConfirmed((prev) => {
      const next = new Set(prev);
      if (next.has(key)) next.delete(key);
      else next.add(key);
      return next;
    });

  return (
    <Field label={V.identity.identifier_label} hint={V.identity.parse_hint}>
      <Input
        mono
        value={raw}
        onChange={(e) => setRaw(e.target.value)}
        placeholder="(01)05012345678900(17)271231(10)LOT42(21)SN0007"
        aria-label={V.identity.identifier_label}
      />
      {segments.length > 0 && (
        <div
          style={{
            marginTop: 10,
            border: '1px solid var(--rule)',
            borderRadius: 'var(--radius-md)',
            overflow: 'hidden',
          }}
        >
          <div
            style={{
              fontFamily: 'var(--mono)',
              fontSize: 10,
              letterSpacing: '.06em',
              textTransform: 'uppercase',
              color: 'var(--muted)',
              padding: '8px 12px',
              borderBottom: '1px solid var(--rule)',
            }}
          >
            parsed · confirm before trust
          </div>
          {segments.map((seg, i) => {
            const key = `${seg.ai}:${seg.value}`;
            return (
              <div
                key={key}
                style={{
                  display: 'grid',
                  gridTemplateColumns: '90px 1fr auto auto',
                  gap: 10,
                  alignItems: 'center',
                  padding: '9px 12px',
                  borderBottom: i < segments.length - 1 ? '1px solid var(--rule-soft)' : 0,
                }}
              >
                <span style={{ fontFamily: 'var(--mono)', fontSize: 11, color: 'var(--muted)' }}>{seg.label}</span>
                <span style={{ fontFamily: 'var(--mono)', fontSize: 12.5 }}>{seg.value}</span>
                <OCRConfidence conf={seg.confidence} />
                <ConfirmButton done={confirmed.has(key)} onClick={() => toggle(key)} />
              </div>
            );
          })}
        </div>
      )}
    </Field>
  );
}
